import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { LucideModule } from '@shared/modules/lucide.module';
import { SharedModule } from '@shared/modules/shared.module';
import { FileUpload } from 'primeng/fileupload';
import { FileItemComponent } from '../file-item/file-item.component';

@Component({
	selector: 'app-file-upload',
	imports: [SharedModule, FileItemComponent, LucideModule],
	templateUrl: './file-upload.component.html',
	styleUrl: './file-upload.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FileUploadComponent),
			multi: true,
		},
	],
})
export class FileUploadComponent extends ControlValueAccessorBase<File[]> implements OnInit {
	@ViewChild('fileUploadRef') fileUpload!: FileUpload;
	@Input() files: File[] = [];
	responsiveOptions: any[] | undefined;
	isDragging = false;

	@Input() placeholder = '';

	@Output() fileSelected = new EventEmitter<File[]>();

	ngOnInit() {
		this.responsiveOptions = [
			{
				breakpoint: '1199px',
				numVisible: 2,
				numScroll: 1,
			},
			{
				breakpoint: '844px',
				numVisible: 1,
				numScroll: 1,
			},
		];
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = true;
	}

	onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;
	}

	onDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;

		const droppedFiles = event.dataTransfer?.files;
		if (droppedFiles && droppedFiles.length > 0) {
			const filesArray = Array.from(droppedFiles);
			this.files = [...this.files, ...filesArray];
			this.fileSelected.emit(this.files);
			this.value = this.files;

			// Update PrimeNG's internal file list
			if (this.fileUpload) {
				this.fileUpload.files = [...this.fileUpload.files, ...filesArray];
			}
		}
	}

	/**
	 * Opens the PrimeNG file upload dialog manually.
	 * This method is triggered when the user clicks on the "Paperclip" icon.
	 */
	triggerFileInput() {
		if (this.fileUpload) {
			this.fileUpload.choose();
		}
	}

	onFileChange(event: any): void {
		const files = event.target.files;
		if (files.length > 0) {
			this.fileSelected.emit(Array.from(files));
		}
	}

	/**
	 * Handles the file selection event.
	 * Adds the selected files to the uploadedFiles list.
	 * @param event File selection event containing the list of files.
	 */
	onFileSelect(event: any) {
		this.files = [...event.files]; // Ensure a new reference to the array
		this.fileSelected.emit(this.files);
		this.value = this.files;
	}

	/**
	 * Handles file upload logic (to be implemented with API integration).
	 * Currently, it only logs the uploaded files.
	 * @param event File upload event containing the list of uploaded files.
	 */
	uploadFiles(_event: any) {
		// Upload logic to be implemented with API integration
	}

	/**
	 * Removes a selected file from both the local list and PrimeNG's internal list.
	 * Ensures that the file is properly removed from the UI and the upload queue.
	 * @param file The file to be removed.
	 */
	removeFile(file: File) {
		const index = this.fileUpload.files.indexOf(file);
		if (index !== -1) {
			this.fileUpload.files.splice(index, 1);
		}

		this.files = this.files.filter((f) => f !== file);
		this.fileSelected.emit(this.files);
		this.value = this.files;

		this.fileUpload.onRemove.emit({ originalEvent: new Event('remove'), file });
	}

	override writeValue(value: File[]): void {
		if (Array.isArray(value)) {
			this.files = value;
		} else {
			this.files = [];
		}
	}

	get validFiles(): boolean {
		return this.files.length > 0 && this.files.every((file) => file.name && file.size);
	}
}
